import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, SlidersHorizontal } from 'lucide-react';
import { api } from '@/lib/api';
import { ProductCard } from '@/components/product/ProductCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export function ProductsPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');

    // Fetch Categories
    const { data: categories } = useQuery({
        queryKey: ['categories'],
        queryFn: () => api.get<any[]>('/categories'),
    });

    // Fetch Products with filters
    const { data: products, isLoading } = useQuery({
        queryKey: ['products', searchTerm, selectedCategory],
        queryFn: () => {
            const params = new URLSearchParams();
            if (searchTerm) params.append('q', searchTerm);
            if (selectedCategory && selectedCategory !== 'all') params.append('categoryId', selectedCategory);
            return api.get<any[]>(`/products?${params.toString()}`);
        },
    });

    return (
        <div className="py-12 animate-fade-in text-foreground">
            <div className="container mx-auto px-4">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Filters */}
                    <aside className="lg:w-64 shrink-0 space-y-8">
                        <div className="rounded-lg border bg-card p-6 shadow-sm">
                            <h2 className="font-semibold text-lg flex items-center gap-2 mb-4">
                                <SlidersHorizontal className="h-5 w-5" />
                                Filters
                            </h2>

                            {/* Category Filter */}
                            <div>
                                <h3 className="font-medium text-sm mb-3">Category</h3>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="category"
                                            value="all"
                                            checked={selectedCategory === 'all'}
                                            onChange={() => setSelectedCategory('all')}
                                            className="w-4 h-4 text-primary accent-primary"
                                        />
                                        <span className="text-sm">All Products</span>
                                    </label>
                                    {categories?.map((cat) => (
                                        <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="category"
                                                value={cat.id}
                                                checked={selectedCategory === cat.id}
                                                onChange={() => setSelectedCategory(cat.id)}
                                                className="w-4 h-4 text-primary accent-primary"
                                            />
                                            <span className="text-sm">{cat.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Products Grid */}
                    <div className="flex-1">
                        {/* Search Bar */}
                        <div className="mb-8 flex gap-4">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    type="text"
                                    placeholder="Search products..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                        </div>

                        {/* Products */}
                        {isLoading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <div key={i} className="aspect-[4/5] rounded-xl bg-muted animate-pulse" />
                                ))}
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {products?.map((product) => (
                                        <ProductCard key={product.id} product={product} />
                                    ))}
                                </div>
                                {products?.length === 0 && (
                                    <div className="text-center py-20">
                                        <p className="text-lg text-muted-foreground">No products found matching your criteria.</p>
                                        <Button variant="link" onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}>Clear Filters</Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
